/** @jsxImportSource theme-ui */
import { useEffect, useState } from "react";
import { Box, Text, Spinner } from "theme-ui";
import SingleResult from "./SingleResult";
import { ImageRequest } from "../types/imagerequest";

interface ResultAreaProps {
    isGeneratingResponse: boolean;
    result: ImageRequest | undefined;
    isAdmin?: boolean;
    onDeleteSuccess?: () => void;
}

const ResultArea = ({ isGeneratingResponse, result, isAdmin, onDeleteSuccess }: ResultAreaProps) => {
    const [timeoutReached, setTimeoutReached] = useState(false);

    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (isGeneratingResponse) {
            setTimeoutReached(false); // Reset timeout if generating starts
            timer = setTimeout(() => {
                setTimeoutReached(true);
            }, 90000); // 1 minute timeout
        }

        return () => {
            if (timer) {
                clearTimeout(timer); // Clear timeout on unmount or isGeneratingResponse change
            }
        };
    }, [isGeneratingResponse]);

    const handleDeleteSuccess = (deletedId: string) => {
        // Call the parent's callback to clear the result
        if (onDeleteSuccess) {
            onDeleteSuccess();
        }
    };

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: [10, 12],
                textAlign: "center",
            }}
        >
            <Text sx={{ fontSize: [5, 7], fontWeight: "semiBold", mb: 2 }}>
                Result
            </Text>
            <Text sx={{ fontSize: [1, 5], color: "secondaryText" }}>
                Here's what we found based on your image.
            </Text>

            {isGeneratingResponse && !timeoutReached && (
                <Spinner sx={{ color: "primaryText", mb: 12, mt: 13 }} />
            )}

            {isGeneratingResponse && timeoutReached && (
                <Box
                    sx={{
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column", // Ensures items stack vertically
                        alignItems: "center",
                        mt: 12,
                    }}
                >
                    <Text sx={{ fontSize: [3, 5], fontWeight: "semiBold" }}>
                        We couldn't retrieve results in time.
                    </Text>
                    <Text sx={{ fontSize: [3, 5], fontWeight: "semiBold", mb: 2 }}>
                        Please try later.
                    </Text>
                </Box>
            )}

            {!isGeneratingResponse && result && (
                <SingleResult
                    result={result}
                    isAdmin={isAdmin}
                    onDeleteSuccess={handleDeleteSuccess}
                />
            )}
        </Box>
    );
};

export default ResultArea;